import React, { useState, useEffect, useCallback } from "react";
import { Collapse } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faBox,
  faTruck,
  faStore,
  faCreditCard,
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import AccountSidebar from "../../components/AccountSidebar/AccountSidebar";
import { getOrderDetailApi } from "../../services/api.services";
import { language } from "../../languageProvider/language/language";
import { defaultLanguage } from "../../helper/utility";
import {
  showVatBreakdown,
  showVatPercentage,
  showPriceWithAndWithoutVat,
  formatVatRate,
  parseAmount,
} from "../../helper/vat";
import "../Profile.page.css";

const STATUS_COLORS = {
  0: "#ffc107",
  1: "#17a2b8",
  2: "#28a745",
  3: "#dc3545",
};

function OrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsOpen, setProductsOpen] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOrderDetailApi(parseInt(orderId), null);
      if (res.status === 200 && res.data) {
        setOrder(res.data);
      } else {
        setError(res.message || "Errore nel caricamento dell'ordine");
      }
    } catch (err) {
      setError("Errore nel caricamento dell'ordine");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // get-order restituisce per ogni riga il gia' calcolato: current_price e'
  // il TOTALE della riga IVA inclusa (quantita' compresa), imponibile_riga e
  // iva_riga lo scompongono. Nessun ricalcolo lato client: per un ordine
  // storico la verita' e' quella registrata dal backend.
  const orderItems = (order?.products || []).map((product) => ({
    ...product,
    lineTotal: parseAmount(product.current_price),
    lineTaxable: parseAmount(product.imponibile_riga),
    lineVat: parseAmount(product.iva_riga),
    rate: parseAmount(product.iva),
    quantity: parseFloat(product.quantity) || 1,
  }));

  const orderShipping = parseAmount(order?.shipping_cost);
  const orderCodFee = parseAmount(order?.cod_fee);
  const orderTotalWithCod = parseAmount(order?.total_with_cod);

  const orderTaxable = orderItems.reduce((sum, i) => sum + i.lineTaxable, 0);
  const orderVat = orderItems.reduce((sum, i) => sum + i.lineVat, 0);
  const orderItemCount = orderItems.reduce((sum, i) => sum + i.quantity, 0);

  // Il breakdown si mostra solo se il backend ha davvero scomposto le righe:
  // sui volantini senza aliquote imponibile_riga e iva_riga non arrivano.
  const hasVatData = orderItems.some((i) => i.lineTaxable > 0 || i.lineVat > 0);
  const showBreakdown = showVatBreakdown() && hasVatData;

  // Contrassegno: come nel checkout resta fuori dal Totale dell'ordine e
  // chiude con la somma consegnata al corriere (total_with_cod).
  const showCodFee = orderCodFee > 0 && orderTotalWithCod > 0;

  return (
    <div className="up-page">
      <div className="up-topbar">
        <div className="up-topbar__left">
          <div className="up-topbar__back" onClick={() => navigate("/profilo/ordini")}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
          <h1 className="up-topbar__title">
            {order ? `Ordine #${order.numero_ordine}` : language[defaultLanguage].ordini}
          </h1>
        </div>
      </div>

      <div className="up-layout">
        <AccountSidebar />
        <div className="up-main">
          {loading && (
            <div className="up-card" style={{ textAlign: "center", padding: "40px" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.5rem", color: "#999" }} />
            </div>
          )}

          {error && (
            <div className="up-card" style={{ textAlign: "center", color: "#dc3545" }}>
              <p>{error}</p>
              <button className="up-btn up-btn--outline" onClick={fetchOrder}>
                Riprova
              </button>
            </div>
          )}

          {order && !loading && (
            <>
              {/* Status + Info */}
              <div className="up-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
                  <div>
                    <h2 className="up-card__title" style={{ marginBottom: "4px" }}>
                      Ordine #{order.numero_ordine}
                    </h2>
                    <span style={{ fontSize: "0.85rem", color: "#999" }}>
                      {formatDate(order.request_datetime)}
                    </span>
                  </div>
                  <span style={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: STATUS_COLORS[order.status] || "#999",
                    backgroundColor: (STATUS_COLORS[order.status] || "#999") + "18",
                    padding: "5px 14px",
                    borderRadius: "12px",
                  }}>
                    {order.status_label}
                  </span>
                </div>
              </div>

              {/* Delivery + Payment info */}
              <div className="up-card">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <FontAwesomeIcon
                      icon={order.delivery_method === 0 ? faStore : faTruck}
                      style={{ color: "#888", marginTop: "3px" }}
                    />
                    <div>
                      <div style={{ fontSize: "0.8rem", color: "#999", textTransform: "uppercase", fontWeight: 600, marginBottom: "2px" }}>
                        Consegna
                      </div>
                      <div style={{ fontSize: "0.92rem", color: "#333" }}>
                        {order.delivery_method_label}
                      </div>
                      {order.delivery_datetime && (
                        <div style={{ fontSize: "0.82rem", color: "#888" }}>
                          {formatDate(order.delivery_datetime)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <FontAwesomeIcon icon={faCreditCard} style={{ color: "#888", marginTop: "3px" }} />
                    <div>
                      <div style={{ fontSize: "0.8rem", color: "#999", textTransform: "uppercase", fontWeight: 600, marginBottom: "2px" }}>
                        Pagamento
                      </div>
                      <div style={{ fontSize: "0.92rem", color: "#333" }}>
                        {order.payment_method_label}
                      </div>
                      <div style={{ fontSize: "0.82rem", color: order.paid ? "#28a745" : "#ffc107" }}>
                        {order.paid_label}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Point of sale */}
              {order.point_of_sale && (
                <div className="up-card">
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: "#888", marginTop: "3px" }} />
                    <div>
                      <div style={{ fontSize: "0.8rem", color: "#999", textTransform: "uppercase", fontWeight: 600, marginBottom: "2px" }}>
                        Punto vendita
                      </div>
                      <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#333" }}>
                        {order.point_of_sale.name}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#888" }}>
                        {order.point_of_sale.address}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Products accordion */}
              {order.products && order.products.length > 0 && (
                <div className="up-card" style={{ padding: 0, overflow: "hidden" }}>
                  <div
                    onClick={() => setProductsOpen(!productsOpen)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "16px 24px",
                      cursor: "pointer",
                    }}
                  >
                    <h2 className="up-card__title" style={{ margin: 0 }}>
                      <FontAwesomeIcon icon={faBox} className="up-card__icon" style={{ marginRight: "8px" }} />
                      {/* Conteggio in pezzi, non in righe: e' lo stesso
                          criterio del checkout ("Mostra prodotti") e della
                          riga Imponibile qui sotto. */}
                      Prodotti ({orderItemCount})
                    </h2>
                    <FontAwesomeIcon
                      icon={productsOpen ? faChevronUp : faChevronDown}
                      style={{ color: "#bbb", fontSize: "0.85rem" }}
                    />
                  </div>
                  <Collapse isOpen={productsOpen}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "0 24px 20px" }}>
                      {orderItems.map((product, idx) => (
                        <div key={idx} style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 12px",
                          borderRadius: "10px",
                          border: "1px solid #f0f0f0",
                        }}>
                          <div style={{
                            width: "50px",
                            height: "50px",
                            minWidth: "50px",
                            borderRadius: "8px",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}>
                            {product.image_file ? (
                              <img src={product.image_file} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <FontAwesomeIcon icon={faBox} style={{ color: "#ccc" }} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#222" }}>
                              {product.field1 || product.description}
                            </div>
                            {product.variety && (
                              <div style={{ fontSize: "0.8rem", color: "#999" }}>{product.variety}</div>
                            )}
                            <div style={{ fontSize: "0.82rem", color: "#888" }}>
                              Qtà: {product.quantity} {product.grocery_label || ""}
                              {/* Qui l'importo e' sempre ivato, quindi si
                                  espone l'aliquota e non il regime del
                                  listino: "IVA Esclusa" accanto a un prezzo
                                  ivato sarebbe fuorviante. */}
                              {showVatPercentage() && product.rate > 0 &&
                                ` · IVA ${formatVatRate(product.rate)}%`}
                            </div>
                            {/* show_price_with_and_without_vat: l'altra faccia
                                del prezzo, cioe' l'imponibile della riga. */}
                            {showPriceWithAndWithoutVat() &&
                              product.lineTaxable > 0 && (
                                <div style={{ fontSize: "0.78rem", color: "#999" }}>
                                  {product.lineTaxable.toFixed(2)} € IVA Esclusa
                                </div>
                              )}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#222" }}>
                              € {product.lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              )}

              {/* Totals — con show_vat_breakdown il riepilogo si apre in
                  imponibile + IVA, come nel checkout. La spedizione non e'
                  ivata, quindi resta una voce a se'. */}
              <div className="up-card">
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {showBreakdown && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#666" }}>
                        <span>
                          Imponibile ({orderItemCount}{" "}
                          {orderItemCount === 1 ? "prodotto" : "prodotti"})
                        </span>
                        <span>€ {orderTaxable.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#666" }}>
                        <span>IVA</span>
                        <span>€ {orderVat.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {orderShipping > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", color: "#666" }}>
                      <span>Spedizione</span>
                      <span>€ {orderShipping.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "#222",
                    paddingTop: "8px",
                    borderTop: "1px solid #f0f0f0",
                  }}>
                    <span>Totale</span>
                    <span>{order.total_formatted || `€ ${order.total}`}</span>
                  </div>
                  {/* Contrassegno: fuori dal Totale (si paga in contanti alla
                      consegna), esattamente come nel riepilogo del checkout. */}
                  {showCodFee && (
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      paddingTop: "8px",
                      paddingLeft: "10px",
                      borderTop: "1px dashed #e0d6b0",
                      fontSize: "0.82rem",
                      color: "#7a5b00",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>+ Contrassegno (in contanti alla consegna)</span>
                        <span>€ {orderCodFee.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                        <span>Totale alla consegna</span>
                        <span>€ {orderTotalWithCod.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderDetailPage;
